import { useState, useEffect } from 'react';
import { User, Users, UserCheck } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import omnichannelService from '../../services/omnichannelService';
import { useAuthContext } from '../../contexts/AuthContext';

interface Agent {
  id: number;
  name: string;
  email: string;
  active_assignments: number;
  max_concurrent_conversations: number;
  is_available: boolean;
}

interface AgentGroup {
  group_id: number;
  group_name: string;
  agents: Agent[];
}

interface AgentAssignmentDropdownProps {
  conversationId: string;
  channelId: string;
  currentAssignedTo?: number;
  onAssignmentChange: () => void;
}

export default function AgentAssignmentDropdown({
  conversationId,
  channelId,
  currentAssignedTo,
  onAssignmentChange
}: AgentAssignmentDropdownProps) {
  const { theme } = useTheme();
  const { user } = useAuthContext();
  const [agentGroups, setAgentGroups] = useState<AgentGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    loadAvailableAgents();
  }, [channelId]);

  const loadAvailableAgents = async () => {
    try {
      setLoading(true);
      const groups = await omnichannelService.getAvailableAgents(channelId);
      setAgentGroups(groups);
    } catch (error) {
      console.error('❌ Failed to load available agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (agentId: number) => {
    try {
      setAssigning(true);
      await omnichannelService.assignConversation(conversationId, agentId);
      onAssignmentChange();
    } catch (error) {
      console.error('Failed to assign conversation:', error);
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignToMe = async () => {
    try {
      setAssigning(true);
      await omnichannelService.assignToMe(conversationId);
      onAssignmentChange();
    } catch (error) {
      console.error('Failed to assign to me:', error);
    } finally {
      setAssigning(false);
    }
  };

  const handleAutoAssign = async () => {
    try {
      setAssigning(true);
      await omnichannelService.autoAssignConversation(conversationId);
      onAssignmentChange();
    } catch (error) {
      console.error('Failed to auto-assign:', error);
    } finally {
      setAssigning(false);
    }
  };

  const getCurrentAgentName = () => {
    if (!currentAssignedTo) return 'Unassigned';

    for (const group of agentGroups) {
      const agent = group.agents.find((a) => a.id === currentAssignedTo);
      if (agent) return agent.name;
    }

    return `Agent #${currentAssignedTo}`;
  };

  const isAssignedToMe = currentAssignedTo === user?.id;

  return (
    <div className="relative group">
      <button
        disabled={assigning}
        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm border transition-colors ${
          theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        } ${assigning ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {currentAssignedTo ? <UserCheck className="h-4 w-4" /> : <User className="h-4 w-4" />}
        <span>{getCurrentAgentName()}</span>
      </button>

      {/* Dropdown Menu */}
      <div
        className={`absolute right-0 mt-1 w-72 rounded-lg shadow-lg border z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}
      >
        {/* Quick Actions */}
        <div className={`p-2 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={handleAssignToMe}
            disabled={assigning || isAssignedToMe}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
              theme === 'dark'
                ? 'hover:bg-gray-700 text-gray-200'
                : 'hover:bg-gray-100 text-gray-700'
            } ${assigning || isAssignedToMe ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <UserCheck className="h-4 w-4" />
            <span>Assign to Me</span>
          </button>

          <button
            onClick={handleAutoAssign}
            disabled={assigning}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm transition-colors ${
              theme === 'dark'
                ? 'hover:bg-gray-700 text-gray-200'
                : 'hover:bg-gray-100 text-gray-700'
            } ${assigning ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Users className="h-4 w-4" />
            <span>Auto-assign</span>
          </button>
        </div>

        {/* Agent Groups */}
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : agentGroups.length === 0 ? (
            <div
              className={`p-4 text-center text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              No agents available
            </div>
          ) : (
            agentGroups.map((group) => (
              <div
                key={group.group_id}
                className={`p-2 ${
                  theme === 'dark' ? 'border-b border-gray-700' : 'border-b border-gray-200'
                }`}
              >
                {/* Group Header */}
                <div
                  className={`px-2 py-1 text-xs font-semibold uppercase ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {group.group_name} ({group.agents.filter((a) => a.is_available).length} available)
                </div>

                {/* Agents */}
                <div className="space-y-1 mt-1">
                  {group.agents.map((agent) => (
                    <button
                      key={agent.id}
                      onClick={() => handleAssign(agent.id)}
                      disabled={assigning || !agent.is_available || agent.id === currentAssignedTo}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors ${
                        agent.id === currentAssignedTo
                          ? theme === 'dark'
                            ? 'bg-indigo-900/30 text-indigo-300'
                            : 'bg-indigo-50 text-indigo-700'
                          : agent.is_available
                            ? theme === 'dark'
                              ? 'hover:bg-gray-700 text-gray-200'
                              : 'hover:bg-gray-100 text-gray-700'
                            : theme === 'dark'
                              ? 'text-gray-600 cursor-not-allowed'
                              : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{agent.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {agent.id === currentAssignedTo && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              theme === 'dark'
                                ? 'bg-indigo-900 text-indigo-200'
                                : 'bg-indigo-100 text-indigo-700'
                            }`}
                          >
                            Current
                          </span>
                        )}
                        <span
                          className={`text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          {agent.active_assignments}/{agent.max_concurrent_conversations}
                        </span>
                        {!agent.is_available && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              theme === 'dark'
                                ? 'bg-red-900 text-red-200'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            Unavailable
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
